<template>
    <div
        ref="container"
        :style="containerStyle"
        @dragover="dragOver($event)"
        @drop="drop($event)"
        class="sceneContainer"
    >
        <v-stage
            ref="stage"
            :config="stageConfig"
            @dragmove="syncViewportPosition($event.currentTarget.getPosition())"
            @mousemove="emitMouseMove($event.currentTarget.getPointerPosition())"
            @wheel="onWheelZoom($event)"
        >
            <GameMat :renderContext="renderContext" :stage="stage"></GameMat>
            <v-layer ref="tokens">
                <PlayToken
                    ref="playTokens"
                    v-for="entity in sceneTokens"
                    :key="entity.id"
                    :entity="entity"
                    :gridSpace="renderContext.gridSpace"
                    @moved="onTokenMoved"
                    @selected="onTokenSelected"
                    @deselected="onTokenDeselected"
                    @dblclick="onTokenEdited"
                />
            </v-layer>
        </v-stage>
        <CharacterEditorWindow ref="editor" @onChange="onCharacterChanged" />
    </div>
</template>

<script>
import Vue from 'vue';
import VueKonva from 'vue-konva';
import { mapGetters, mapState } from 'vuex';

import xy from '@/utils/xy';
import panzoom from '@/utils/panzoom';

import SquareGrid from '@/core/grid/SquareGrid.js';
import GameMat from './GameMat.vue';
import CharacterEditorWindow from '@/components/character/EditorWindow.vue';
import PlayToken from './PlayToken';
import { SCENE } from '@/core/scene/constants.js';

Vue.use(VueKonva);

export default {
    components: {
        CharacterEditorWindow: CharacterEditorWindow,
        GameMat: GameMat,
        PlayToken: PlayToken,
    },
    data() {
        return {
            renderContext: {
                gridSpace: null,
                pixelRatio: 1,
            },
            viewport: {
                pos: { x: 0, y: 0 },
                size: { width: null, height: null },
                bounds: { n: 0, e: 0, s: 0, w: 0 },
                panzoom: new panzoom(),
            },
            selectedTokenId: '',
        };
    },
    computed: {
        ...mapState('gamestate', {
            entities: 'entities',
            activeScene: 'activeScene',
        }),
        ...mapGetters('gamestate/entities', {
            getEntityByRef: 'getEntityByRef',
            entities: 'tokens',
        }),
        ...mapGetters('collections', {
            getItem: 'getItem',
            scenes: 'scenes',
        }),
        containerStyle() {
            return {
                'background-color': this.stage.color || '#121212',
            };
        },
        sceneTokens() {
            const sceneId = this.activeScene ? this.activeScene._id : null;
            if (!sceneId) return [];
            return this.entities.filter(function(entity) {
                return !entity.token.scene || entity.token.scene === sceneId;
            });
        },
        stageConfig: function() {
            return {
                draggable: true,
                size: this.viewport.size,
            };
        },
        stage: function() {
            return this.activeScene ? this.activeScene.stage : getDefaultStage();
        },
    },
    watch: {
        'stage.grid': {
            handler({ type, size }) {
                if (type !== 'square') {
                    if (process.env.NODE_ENV !== 'production') {
                        console.warn('Only square grids are currently supported');
                    }
                    type = 'square';
                }
                size = SCENE.MAGIC_SCALE;
                let gridSpace = this.renderContext.gridSpace;
                this.renderContext.gridSpace =
                    gridSpace && gridSpace.type === type ? gridSpace.withSize(size) : new SquareGrid(size);
            },
            deep: true,
            immediate: true,
        },
    },
    created() {
        this.renderContext.pixelRatio = window.devicePixelRatio || 1;
    },
    mounted() {
        window.addEventListener('keyup', this.onKeyUp);
    },
    destroyed() {
        window.removeEventListener('keyup', this.onKeyUp);
    },
    methods: {
        emitMouseMove(sPos) {
            const offset = this.viewport.pos;
            const stagePos = {
                x: sPos.x + offset.x,
                y: sPos.y + offset.y,
            };
            this.$emit('mousemove', {
                stage: stagePos,
                grid: this.renderContext.gridSpace.stageToGrid(stagePos),
            });
        },
        fitToContainer() {
            const container = this.$refs.container;
            const rect = container.getClientRects()[0];
            this.viewport.size = {
                height: Math.floor(rect.height),
                width: Math.floor(rect.width),
            };
        },
        onTokenMoved: function({ entity, position }) {
            const gridPos = this.renderContext.gridSpace.stageToGrid(position);
            const snappedPos = this.renderContext.gridSpace.gridToAnchor(gridPos);

            this.$store.dispatch('gamestate/resolveAction', {
                op: 'replace',
                path: '/entities/' + entity.id + '/token/pos',
                value: snappedPos,
            });

            this.$emit('tokenmove', {
                entity,
                pos: snappedPos,
            });
        },
        onTokenSelected: function({ entity }) {
            if (this.selectedTokenId === '') {
                this.selectedTokenId = entity.id;
            } else if (this.selectedTokenId !== entity.id) {
                let oldSelectedTokenId = this.selectedTokenId;
                let oldToken = this.$refs['playTokens'].find(token => token.entity.id === oldSelectedTokenId);
                oldToken.selected = false;
                this.selectedTokenId = entity.id;
            } else {
                return;
            }
            this.updateSelectedTokenStore();
        },
        onTokenDeselected: function() {
            this.selectedTokenId = '';
            this.updateSelectedTokenStore();
        },
        onTokenEdited: function({ character }) {
            this.$refs.editor.character = character;
            this.$refs.editor.isOpen = true;
        },
        onCharacterChanged: function({ characterId, patch }) {
            this.$emit('onCharacterChanged', { characterId, patch });
        },
        updateSelectedTokenStore() {
            this.$store.commit('gamestate/selectedTokenUpdate', this.selectedTokenId);
        },
        onKeyUp: function(event) {
            if (event.key == 'Delete') {
                this.onDelete();
            }
        },
        onDelete() {
            var tokenIdToBeDeleted = this.selectedTokenId;
            this.onTokenDeselected();
            this.$store.dispatch('live/removeCharacter', {
                characterId: tokenIdToBeDeleted,
            });
        },
        onWheelZoom(event) {
            const stage = event.currentTarget;
            const delta = -event.evt.deltaY;

            if (delta == 0) {
                return;
            }

            const viewAnchor = xy(stage.getPointerPosition());

            let baseScale = stage.scaleX();
            let newScale = stage.scaleX() * (1 + delta / 500);

            let gridSpace = this.renderContext.gridSpace;

            // Clamp new grid size between 10 and 8 times base size
            let scaledGridSize = Math.max(10, Math.min(gridSpace.base.size * 8, gridSpace.size * newScale));
            if (scaledGridSize == gridSpace.size) return;

            // Apply grid size clamping back to scale value
            newScale = scaledGridSize / gridSpace.size;

            // Calculate target stage position relative to anchor
            let offset = viewAnchor.subtract(stage.position());
            let newPos = viewAnchor.subtract(offset.multiply(newScale / baseScale));

            stage.position(newPos);
            stage.scale(xy(newScale, newScale));
            stage.draw();

            // Calculate correction required to have integer grid size
            let finalGridSize = delta > 0 ? Math.ceil(scaledGridSize) : Math.floor(scaledGridSize);
            let correctedScale = finalGridSize / gridSpace.size;
            let correctedPos = viewAnchor.subtract(offset.multiply(correctedScale / baseScale));

            this.viewport.panzoom.startCorrection({
                node: stage,
                position: { x: Math.round(correctedPos.x), y: Math.round(correctedPos.y) },
                scale: correctedScale,
                finish: () => {
                    stage.scale({ x: 1, y: 1 });
                    this.renderContext.gridSpace = gridSpace.withSize(finalGridSize);
                    this.syncViewportPosition();
                },
            });
        },
        /**
         * Synchronize viewport position stete with position of stage node.
         *
         * @param {{x: number, y: number}} [nodePosition] - If provided, use this
         *     node position instead of getting it from the node.
         */
        syncViewportPosition(nodePosition) {
            nodePosition = nodePosition || this.$refs.stage.getNode().position();
            this.viewport.pos = xy(nodePosition).reflect();
        },

        dragOver(e) {
            if (this.$store.getters['dropper/ready']) {
                e.preventDefault();
            }
        },

        drop(e) {
            if (this.$store.getters['dropper/ready']) {
                e.preventDefault();
                const stagePos = this.$refs.stage.getNode().position();
                const gridPos = this.renderContext.gridSpace.stageToGrid({
                    x: e.clientX - stagePos.x,
                    y: e.clientY - stagePos.y,
                });
                const snappedPos = this.renderContext.gridSpace.gridToAnchor(gridPos);
                const character = this.$store.getters['dropper/draggedItem'];
                this.$store.dispatch('live/addCharacter', {
                    image: character.asset.src,
                    pos: snappedPos,
                    sceneId: this.activeScene ? this.activeScene._id : null,
                });
                this.$store.dispatch('dropper/reset');
            }
        },
    },
};

const getDefaultStage = () => {
    return {
        bounds: { nw: { q: 0, r: 0 }, se: { q: 49, r: 49 } },
        color: '#121212',
        grid: { type: 'square', size: 50 },
    };
};
</script>

<style>
.sceneContainer {
    height: 100%;
    width: 100%;
    flex: 1 1 auto;
}
</style>
